import React, { useState, useEffect, useReducer, useContext, useRef } from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TablePagination from "@material-ui/core/TablePagination";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Tooltip from "@material-ui/core/Tooltip";
import Avatar from "@material-ui/core/Avatar";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchIcon from "@material-ui/icons/Search";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import IconButton from "@material-ui/core/IconButton";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import Checkbox from "@material-ui/core/Checkbox";
import GetAppIcon from "@material-ui/icons/GetApp";
import api, { getProfilePicUrl } from "../../services/api";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";
import ImportContactsResultModal from "../../components/ImportContactsResultModal";
import CancelIcon from "@material-ui/icons/Cancel";
import { i18n } from "../../translate/i18n";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";
import { generateColor } from "../../helpers/colorGenerator";
import { getInitials } from "../../helpers/getInitials";


const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    const newContacts = [];

    // ✅ CORREGIR: Validar que contacts es un array válido
    if (!Array.isArray(contacts)) {
      console.warn("⚠️ LOAD_CONTACTS: Payload no es un array:", contacts);
      return state;
    }

    contacts.forEach((contact) => {
      // ✅ CORREGIR: Validar que cada contact tiene id
      if (!contact || !contact.id) {
        console.warn("⚠️ LOAD_CONTACTS: Contact inválido omitido:", contact);
        return; // ✅ Continuar con el siguiente contacto
      }
      
      const contactIndex = state.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        state[contactIndex] = contact;
      } else {
        newContacts.push(contact);
      }
    });

    return [...state, ...newContacts];
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    
    // ✅ CORREGIR: Validar que contact existe y tiene id
    if (!contact || !contact.id) {
      console.warn("⚠️ UPDATE_CONTACTS: Contact inválido recibido:", contact);
      return state; // ✅ Retornar estado actual sin cambios
    }
    
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      // ✅ CORREGIR: Crear nueva referencia del contacto para forzar re-render
      const updatedState = [...state];
      const oldContact = updatedState[contactIndex];
      updatedState[contactIndex] = { ...contact }; // ✅ Spread para nueva referencia
      console.log("🔄 UPDATE_CONTACTS: Contacto actualizado en reducer:", {
        id: contact.id,
        name: contact.name,
        oldProfilePicUrl: oldContact?.profilePicUrl,
        newProfilePicUrl: contact.profilePicUrl,
        stateLength: updatedState.length
      });
      return updatedState;
    } else {
      console.log("➕ UPDATE_CONTACTS: Contacto nuevo agregado:", {
        id: contact.id,
        name: contact.name,
        profilePicUrl: contact.profilePicUrl
      });
      return [{ ...contact }, ...state]; // ✅ Spread para nueva referencia
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;

    const contactIndex = state.findIndex((c) => c.id === contactId);
    if (contactIndex !== -1) {
      state.splice(contactIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles,
  },
}));

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  // ✅ Estados para paginación frontend
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const fileUploadRef = useRef(null);
  const [, forceUpdate] = useState(0); // ✅ NUEVO: Estado para forzar re-render

  // Estados para selección múltiple
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [actionType, setActionType] = useState(""); // "delete", "export" o "import"
  const [contactCount, setContactCount] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [importResultModalOpen, setImportResultModalOpen] = useState(false);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    // ✅ Resetear paginación frontend al cambiar búsqueda
    setPage(0);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          // ✅ CARGAR TODOS LOS CONTACTOS: Cargar todas las páginas hasta obtener todos
          // Sin límite artificial - carga todos los contactos disponibles
          let allContacts = [];
          let currentPage = 1;
          let hasMorePages = true;
          
          while (hasMorePages) {
            const { data } = await api.get("/contacts/", {
              params: { searchParam, pageNumber: currentPage.toString() },
            });
            
            allContacts = [...allContacts, ...data.contacts];
            hasMorePages = data.hasMore;
            currentPage++;
            
            // ✅ Sin límite: carga todos los contactos disponibles
            // Si hay muchos contactos (ej: 5000+), puede tomar unos segundos
            // pero garantiza que se muestren TODOS
          }
          
          dispatch({ type: "LOAD_CONTACTS", payload: allContacts });
          setLoading(false);
        } catch (err) {
          toastError(err);
          setLoading(false);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]); // ✅ Remover pageNumber de dependencias porque cargamos todas las páginas

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    // ✅ CORREGIR: Validar que socket existe antes de agregar listeners
    if (!socket) {
      console.warn("⚠️ Socket no disponible para companyId:", companyId);
      return;
    }

    const handleContactUpdate = (data) => {
      // ✅ CORREGIR: Validar que data existe y tiene la estructura esperada
      if (!data || !data.action) {
        console.warn("⚠️ Socket contact: Data inválida recibida:", data);
        return;
      }

      if (data.action === "update" || data.action === "create") {
        // ✅ CORREGIR: Validar que data.contact existe y tiene id
        if (data.contact && data.contact.id) {
          console.log("🔄 Socket contact: Actualizando contacto:", {
            id: data.contact.id,
            name: data.contact.name,
            profilePicUrl: data.contact.profilePicUrl,
            contactData: data.contact
          });
          dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
          // ✅ NUEVO: Forzar re-render después de actualizar el estado
          forceUpdate(prev => prev + 1);
          console.log("✅ Socket contact: Contacto actualizado en tiempo real:", data.contact.name);
        } else {
          console.warn("⚠️ Socket contact: Contact inválido recibido:", data.contact);
        }
      }

      if (data.action === "delete") {
        // ✅ CORREGIR: Validar que data.contactId existe
        if (data.contactId) {
          dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
          console.log("✅ Socket contact: Contacto eliminado en tiempo real:", data.contactId);
        } else {
          console.warn("⚠️ Socket contact: contactId no encontrado en data:", data);
        }
      }
    };

    socket.on(`company-${companyId}-contact`, handleContactUpdate);

    return () => {
      // ✅ CORREGIR: Solo remover el listener específico, no desconectar el socket
      if (socket && typeof socket.off === 'function') {
        socket.off(`company-${companyId}-contact`, handleContactUpdate);
      }
    };
  }, [socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  // const handleSaveTicket = async contactId => {
  // 	if (!contactId) return;
  // 	setLoading(true);
  // 	try {
  // 		const { data: ticket } = await api.post("/tickets", {
  // 			contactId: contactId,
  // 			userId: user?.id,
  // 			status: "open",
  // 		});
  // 		history.push(`/tickets/${ticket.id}`);
  // 	} catch (err) {
  // 		toastError(err);
  // 	}
  // 	setLoading(false);
  // };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const hadleEditContact = (contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  };

  const handleDeleteContact = async (contactId) => {
    setLoading(true);
    try {
      await api.delete(`/contacts/${contactId}`);
      
      // ✅ CORREGIR: Actualizar el estado local después de eliminar
      dispatch({ type: "DELETE_CONTACT", payload: contactId });
      
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
      // ✅ Si hay error, forzar recarga para sincronizar con el backend
      dispatch({ type: "RESET" });
    }
    setLoading(false);
    setConfirmOpen(false);
    setDeletingContact(null);
  };

  // Funciones para selección múltiple
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedContacts([]);
      setSelectAll(false);
    } else {
      setSelectedContacts(contacts.map(contact => contact.id));
      setSelectAll(true);
    }
  };

  const handleDeleteSelectedContacts = async () => {
    if (selectedContacts.length === 0) return;
    
    setLoading(true);
    try {
      await api.delete("/contacts/", {
        data: { contactIds: selectedContacts }
      });
      
      // ✅ CORREGIR: Actualizar el estado local después de eliminar múltiples contactos
      selectedContacts.forEach(contactId => {
        dispatch({ type: "DELETE_CONTACT", payload: contactId });
      });
      
      toast.success(i18n.t("contacts.toasts.deletedAll"));
      setSelectedContacts([]);
      setSelectAll(false);
    } catch (err) {
      toastError(err);
      // ✅ Si hay error, forzar recarga para sincronizar con el backend
      dispatch({ type: "RESET" });
    }
    setLoading(false);
    setConfirmOpen(false);
  };

  const handleExportSelectedContacts = () => {
    if (selectedContacts.length === 0) return;
    
    // Filtrar solo los contactos seleccionados
    const contactsToExport = contacts.filter(contact => 
      selectedContacts.includes(contact.id)
    );
    
    // Mostrar mensaje de procesamiento para grandes cantidades
    if (selectedContacts.length > 50) {
      toast.info(i18n.t("contacts.messages.processingExport").replace('{count}', selectedContacts.length));
    }
    
    // Usar setTimeout para no bloquear la UI
    setTimeout(() => {
      try {
        // Crear el CSV con los datos filtrados
        const csvData = contactsToExport.map((contact) => ({ 
          name: contact.name || '', 
          number: contact.number || '', 
          email: contact.email || '' 
        }));
        
        // Crear el contenido CSV con encoding UTF-8 BOM para Excel
        const csvHeader = 'name;number;email\n';
        const csvContent = csvData.map(row => 
          `"${row.name.replace(/"/g, '""')}";"${row.number}";"${row.email}"`
        ).join('\n');
        
        const csvString = '\ufeff' + csvHeader + csvContent; // BOM para UTF-8
        
        // Crear y descargar el archivo
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `whaticket_${selectedContacts.length}_contacts_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        // Limpiar la URL del blob
        URL.revokeObjectURL(url);
        
        toast.success(i18n.t("contacts.messages.exportSuccess"));
      } catch (error) {
        console.error('Error al exportar:', error);
        toast.error(i18n.t("contacts.messages.exportError"));
      }
      
      setConfirmOpen(false);
    }, 100);
  };
  
  const handleCheckAgenda = async () => {
    try {
      toast.info(i18n.t("contacts.messages.checkingAgenda"));
      
      const { data: countData } = await api.get("/contacts/count");
      const count = countData.count;
      
      if (count === 0) {
        toast.warning(i18n.t("contacts.messages.agendaEmpty"));
        return;
      }
      
      setContactCount(count);
      setActionType("import");
      setConfirmOpen(true);
    } catch (err) {
      toastError(err);
    }
  };

  const handleimportContact = async () => {
    try {
      setLoading(true);
      setConfirmOpen(false);
      
      if (!!fileUploadRef.current.files[0]) {
        const formData = new FormData();
        formData.append("file", fileUploadRef.current.files[0]);
        
        // ✅ CORREGIR: Capturar la respuesta del backend con mejor manejo de errores
        const response = await api.request({
          url: `/contacts/upload`,
          method: "POST",
          data: formData,
        });
        
        // ✅ CORREGIR: Validar que la respuesta existe y tiene la estructura esperada
        if (response && response.data) {
          if (response.data.success) {
            const { message } = response.data; // ✅ CORREGIR: Solo usar message, no importedContacts
            toast.success(`✅ ${message}`);
            
            // ✅ CORREGIDO: No hacer RESET porque los sockets ya actualizaron el estado en tiempo real
            // Los contactos ya están visibles gracias a las actualizaciones de socket
          } else {
            toast.error(response.data.message || "Error al importar contactos");
          }
        } else {
          toast.error("Respuesta inválida del servidor");
        }
      } else {
        // ✅ Importar los contactos desde la agenda de WhatsApp
        const response = await api.post("/contacts/import");
        
        // ✅ Mostrar modal con resultados detallados (sin toasts)
        if (response && response.data) {
          setImportResult(response.data);
          setImportResultModalOpen(true);
        }
      }
      
      // ✅ Limpiar estados
      setContactCount(0);
      setActionType("");
      
      // ✅ CORREGIR: No usar history.go(0) que recarga toda la página
      // Los contactos se actualizarán automáticamente vía sockets
    } catch (err) {
      console.error("Error en importación de contactos:", err);
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTemplate = () => {
    try {
      // ✅ CORREGIR: Usar la URL completa del backend
      const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const fileUrl = `${backendUrl}/files/plantilla-watoolx-contact.xlsx`;
      
      // Crear un enlace temporal para descargar el archivo
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = 'plantilla-watoolx-contact.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast.success("Plantilla descargada correctamente");
    } catch (error) {
      console.error('Error al descargar plantilla:', error);
      toast.error("Error al descargar la plantilla");
    }
  };
  
function getDateLastMessage(contact) {
    if (!contact) return null;
    if (!contact.tickets) return null;

    if (contact.tickets.length > 0) {
        const date = new Date(contact.tickets[contact.tickets.length - 1].updatedAt);

        const day = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
        const month = (date.getMonth() + 1) > 9 ? (date.getMonth() + 1) : `0${date.getMonth() + 1}`;
        const year = date.getFullYear().toString().slice(-2);

        const hours = date.getHours() > 9 ? date.getHours() : `0${date.getHours()}`;
        const minutes = date.getMinutes() > 9 ? date.getMinutes() : `0${date.getMinutes()}`;

        return `${day}/${month}/${year} ${hours}:${minutes}`;
    }

    return null;
}

  // ✅ loadMore y handleScroll ya no son necesarios porque cargamos todas las páginas automáticamente

  return (
    <MainContainer className={classes.mainContainer}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ImportContactsResultModal
        open={importResultModalOpen}
        onClose={() => {
          setImportResultModalOpen(false);
          setImportResult(null);
        }}
        result={importResult}
      />
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${
                deletingContact.name
              }?`
            : actionType === "import"
            ? `${i18n.t("contacts.confirmationModal.importTitle")}`
            : actionType === "export" && selectedContacts.length > 0
            ? selectedContacts.length === contacts.length
              ? "Exportar todos los contactos"
              : `Exportar ${selectedContacts.length} contacto${selectedContacts.length > 1 ? 's' : ''}`
            : selectedContacts.length > 0
            ? selectedContacts.length === contacts.length
              ? `${i18n.t("contacts.confirmationModal.deleteAllTitle")}`
              : `Eliminar ${selectedContacts.length} contacto${selectedContacts.length > 1 ? 's' : ''}`
            : `${i18n.t("contacts.confirmationModal.importTitle")}`
        }
        open={confirmOpen}
        onClose={() => {
          setConfirmOpen(false);
          setActionType("");
          setContactCount(0);
        }}
        onConfirm={(e) => {
          if (deletingContact) {
            handleDeleteContact(deletingContact.id);
          } else if (actionType === "import") {
            // ✅ CORREGIR: Priorizar importación cuando actionType es "import"
            // Esto puede ser desde archivo (fileUploadRef) o desde agenda (contactCount > 0)
            handleimportContact();
          } else if (actionType === "export" && selectedContacts.length > 0) {
            handleExportSelectedContacts();
          } else if (selectedContacts.length > 0) {
            // ✅ Solo borrar si NO hay acción de importación pendiente
            handleDeleteSelectedContacts();
          } else {
            handleimportContact();
          }
        }}
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : actionType === "import"
          ? contactCount > 0
            ? i18n.t("contacts.confirmationModal.importAgendaMessage").replace('{count}', contactCount)
            : fileUploadRef.current?.files?.[0]
            ? `${i18n.t("contacts.confirmationModal.importMessage")} ${fileUploadRef.current.files[0].name}`
            : `${i18n.t("contacts.confirmationModal.importMessage")}`
          : actionType === "export" && selectedContacts.length > 0
          ? selectedContacts.length === contacts.length
            ? "¿Está seguro que desea exportar todos los contactos?"
            : `¿Está seguro que desea exportar ${selectedContacts.length} contacto${selectedContacts.length > 1 ? 's' : ''}?`
          : selectedContacts.length > 0
          ? selectedContacts.length === contacts.length
            ? `${i18n.t("contacts.confirmationModal.deleteAllMessage")}`
            : `¿Seguro que desea eliminar ${selectedContacts.length} contacto${selectedContacts.length > 1 ? 's' : ''}? Se perderán todos los tickets relacionados.`
          : `${i18n.t("contacts.confirmationModal.importMessage")}`}
      </ConfirmationModal>
      <MainHeader>
        <Title>
          {i18n.t("contacts.title")}
          <span style={{ 
            marginLeft: '12px', 
            fontSize: '0.85em', 
            fontWeight: 'normal', 
            color: '#666',
            backgroundColor: '#e3f2fd',
            padding: '4px 10px',
            borderRadius: '16px',
            border: '1px solid #90caf9'
          }}>
            {contacts.length} {contacts.length === 1 ? 'contacto' : 'contactos'}
          </span>
        </Title>
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder={i18n.t("contacts.searchPlaceholder")}
            type="search"
            value={searchParam}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ color: "gray" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip
            title="Esta funcionalidad está temporalmente deshabilitada. Baileys 6.7.21 no soporta la obtención directa de contactos. Se habilitará nuevamente cuando haya una versión compatible de Baileys."
            arrow
          >
            <span>
              <Button
                variant="contained"
                color="primary"
                disabled={true}
                onClick={handleCheckAgenda}
              >
                {i18n.t("contacts.buttons.import")}
              </Button>
            </span>
          </Tooltip>
                    <Button
            variant="contained"
            color="primary"
            onClick={() => {
              // ✅ CORREGIR: Limpiar selección de contactos antes de importar para evitar confusión
              setSelectedContacts([]);
              setSelectAll(false);
              fileUploadRef.current.value = null;
              fileUploadRef.current.click();
            }}
      >
        {i18n.t("contacts.buttons.importSheet")}
      </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenContactModal}
          >
            {i18n.t("contacts.buttons.add")}
          </Button>

          <Button
            variant="contained"
            color="secondary"
            disabled={selectedContacts.length === 0}
            onClick={() => {
              setActionType("delete");
              setConfirmOpen(true);
            }}
          >
            {i18n.t("contacts.buttons.delete")}
          </Button>

          <Button
            variant="contained"
            color="primary"
            onClick={() => {
              if (selectedContacts.length === 0) {
                toast.error(i18n.t("contacts.messages.selectToExport"));
                return;
              }
              setActionType("export");
              setConfirmOpen(true);
            }}
          >
            {i18n.t("contacts.buttons.export")}
          </Button>
          <IconButton
            color="primary"
            onClick={handleDownloadTemplate}
            title={i18n.t("contacts.buttons.downloadTemplate")}
            style={{ marginLeft: '8px' }}
          >
            <GetAppIcon />
          </IconButton>

        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper
        className={classes.mainPaper}
        variant="outlined"
      >
        <>
          <input
              style={{ display: "none" }}
              id="upload"
              name="file"
              type="file"
              accept=".xls,.xlsx"
              onChange={() => {
                // ✅ CORREGIR: Configurar actionType para importar y limpiar selección de contactos
                // Esto evita que se confunda con la acción de borrar cuando hay contactos seleccionados
                setActionType("import");
                setSelectedContacts([]); // Limpiar selección para evitar confusión
                setSelectAll(false);
                setConfirmOpen(true);
              }}
              ref={fileUploadRef}
          />
        </>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                  indeterminate={selectedContacts.length > 0 && selectedContacts.length < contacts.length}
                />
              </TableCell>
              <TableCell>PERFIL</TableCell>
              <TableCell>{i18n.t("contacts.table.name")}</TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.whatsapp")}
              </TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.email")}
              </TableCell>
              <TableCell align="center">
              {i18n.t("contacts.table.lastInteraction")}
              </TableCell>
			  <TableCell align="center">{i18n.t("contacts.table.status")}</TableCell>
              <TableCell align="center">
                {i18n.t("contacts.table.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <>
              {/* ✅ PAGINACIÓN: Mostrar solo los contactos de la página actual */}
              {contacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((contact) => (
                <TableRow key={`row-${contact.id}-${contact.profilePicUrl || 'no-pic'}-${contact.name}`}>
                  <TableCell style={{ paddingRight: 0 }}>
                    <Checkbox
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => handleSelectContact(contact.id)}
                    />
                  </TableCell>
                  <TableCell style={{ paddingRight: 0 }}>
                    <Avatar
                      style={{ backgroundColor: generateColor(contact?.number), fontWeight: "bold", color: "white" }}
                      src={contact.profilePicUrl && !contact.profilePicUrl.includes("nopicture.png") ? `${getProfilePicUrl(contact.profilePicUrl)}?v=${Date.now()}` : null}>
                      {getInitials(contact?.name)}
                    </Avatar>
                  </TableCell>
                  <TableCell>{contact.name}</TableCell>
                  <TableCell align="center">{contact.number}</TableCell>
                  <TableCell align="center">{contact.email}</TableCell>
                                    <TableCell align="center">
                                        {getDateLastMessage(contact)}
                                    </TableCell>
                                    <TableCell align="center">
                                        {contact.active ? (
                                            <CheckCircleIcon
                                                style={{ color: "green" }}
                                                fontSize="small"
                                            />
                                        ) : (
                                            <CancelIcon
                                                style={{ color: "red" }}
                                                fontSize="small"
                                            />
                                        )}
                                    </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() => {
                        setContactTicket(contact);
                        setNewTicketModalOpen(true);
                      }}
                    >
                      <WhatsAppIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => hadleEditContact(contact.id)}
                    >
                      <EditIcon />
                    </IconButton>
                    <Can
                      role={user.profile}
                      perform="contacts-page:deleteContact"
                      yes={() => (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setConfirmOpen(true);
                            setDeletingContact(contact);
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      )}
                    />
                  </TableCell>
                </TableRow>
              ))}
              {loading && <TableRowSkeleton avatar columns={3} />}
            </>
          </TableBody>
        </Table>
        
        {/* ✅ PAGINACIÓN: Solo aparece cuando hay resultados */}
        {!loading && contacts.length > 0 && (
          <TablePagination
            component="div"
            count={contacts.length}
            page={page}
            onPageChange={(event, newPage) => {
              setPage(newPage);
            }}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 15, 20, 50, 100]}
            labelRowsPerPage="Registros por página:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
            style={{
              backgroundColor: 'transparent',
              color: 'inherit',
            }}
          />
        )}
      </Paper>
    </MainContainer>
  );
};

export default Contacts;
